import { ReactNode, useState, useEffect } from 'react';
import { useTenantEnv } from '@data/tenants';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export interface InstallPwaProps {
  className?: string;
  siteName?: string;
}

const COOKIE_NAME = 'pwa-install-dismissed';

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
}

function ShareIcon() {
  return (
    <svg className="install-pwa__share-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2L12 14M12 2L8 6M12 2L16 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 14V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InstallIcon() {
  return (
    <svg className="install-pwa__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2L12 14M12 14L8 10M12 14L16 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface InstallBannerProps {
  bannerClassName: string;
  closeButtonClassName?: string;
  onDismiss: () => void;
  icon: ReactNode;
  title: string;
  description: ReactNode;
  buttonLabel: string;
  onButtonClick: () => void;
}

function InstallBanner({
  bannerClassName,
  closeButtonClassName,
  onDismiss,
  icon,
  title,
  description,
  buttonLabel,
  onButtonClick,
}: InstallBannerProps) {
  return (
    <div className={bannerClassName}>
      <button
        className={closeButtonClassName ?? 'install-pwa__close-button text-l'}
        onClick={onDismiss}
        aria-label="Dismiss install banner"
      >
        ×
      </button>
      <div className="install-pwa__content">
        <div className="install-pwa__icon-wrapper">{icon}</div>
        <div className="install-pwa__text-content">
          <h4 className="install-pwa__title">{title}</h4>
          <p className="install-pwa__description small-text">{description}</p>
        </div>
        <button onClick={onButtonClick} className="install-pwa__install-button small-text">
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

export function InstallPwa({ className, siteName }: InstallPwaProps) {
  const { pwaDismissDays } = useTenantEnv();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
    setIsIOS(iOS);

    // Check if user previously dismissed the banner (via cookie)
    const dismissed = getCookie(COOKIE_NAME);
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Check if already installed (running as standalone PWA)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone;
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        setInstallPrompt(null);
        setIsInstalled(true);
      }
    } else if (isIOS) {
      setShowIOSInstructions(true);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setCookie(COOKIE_NAME, 'true', pwaDismissDays);
  };

  const canInstall = (!!installPrompt || isIOS) && !isInstalled && !isDismissed;

  if (!canInstall) return null;

  const displayName = siteName || 'our';
  const bannerClassName = `install-pwa ${className || ''}`.trim();

  if (showIOSInstructions) {
    return (
      <InstallBanner
        bannerClassName={bannerClassName}
        closeButtonClassName="install-pwa__close-button text-l"
        onDismiss={handleDismiss}
        icon={<ShareIcon />}
        title="Add to Home Screen"
        description={
          <>
            Tap the Share button <ShareIcon /> in Safari, then select "Add to Home Screen"
          </>
        }
        buttonLabel="Got it"
        onButtonClick={() => setShowIOSInstructions(false)}
      />
    );
  }

  return (
    <InstallBanner
      bannerClassName={bannerClassName}
      onDismiss={handleDismiss}
      icon={<InstallIcon />}
      title={`Install ${displayName} App`}
      description="Instant access to live scores, tables, news and fixtures. Any time, any device!"
      buttonLabel="Install"
      onButtonClick={handleInstall}
    />
  );
}
